import { useEffect, useRef, useState } from "react";
import axios from "axios";
import {
  ChevronLeft,
  ChevronRight,
  Phone,
  MessageCircle,
  PenLine,
  AlertTriangle,
  Bot,
  Loader2,
} from "lucide-react";
import {
  API,
  FOCUS,
  entetes,
  estAnnulation,
  estNonAutorise,
  messageErreur,
  analyserRdv,
  compteARebours,
  jourCourt,
  dateCourte,
  heureRdv,
  resumeRdv,
  categorie,
  styleStatut,
  libelleStatut,
  stylePaiement,
  capagoIncomplete,
  nomComplet,
  numeroWhatsapp,
  lienTel,
  montant,
  rdvBotASaisir,
} from "./outils";
import { useMaintenant } from "./EcranRecherche";

// Le bot Capago enregistre ses modifications sous « bot » : affiché « Bot Capago ».
const nomAgent = (n) => (String(n).trim().toLowerCase() === "bot" ? "Bot Capago" : n);

const TITRE_SECTION = "m-0 text-xs font-bold text-[#475569] uppercase tracking-[0.04em]";

function Ligne({ libelle, children, dernier }) {
  return (
    <div className={`flex items-center justify-between gap-3 min-h-[44px] py-1.5 ${dernier ? "" : "border-b border-[#F1F5F9]"}`}>
      <span className="text-sm text-[#475569] flex-none">{libelle}</span>
      <span className="text-right min-w-0 break-words">{children}</span>
    </div>
  );
}

function SectionRdv({ p, maintenant }) {
  if (!p.date_rdv) return null;
  const d = analyserRdv(p.date_rdv);
  if (!d) {
    return (
      <section className="bg-white border border-[#E2E8F0] rounded-[14px] p-3.5 text-[15px] font-bold">
        Rendez-vous : {p.date_rdv}
      </section>
    );
  }
  const c = compteARebours(d, maintenant);
  const ton = c.passe
    ? { fond: "#F8FAFC", bord: "#E2E8F0", accent: "#475569" }
    : c.urgent
      ? { fond: "#FEF2F2", bord: "#FCA5A5", accent: "#B91C1C" }
      : c.proche
        ? { fond: "#FFF7ED", bord: "#FDBA74", accent: "#9A3412" }
        : { fond: "#F0FDFA", bord: "#99F6E4", accent: "#115E59" };
  const h = heureRdv(d);
  return (
    <section
      data-testid="alerte-rdv-badge"
      className="rounded-[14px] p-3.5 flex items-center gap-3 border"
      style={{ background: ton.fond, borderColor: ton.bord }}
    >
      <div
        className="flex-none flex flex-col items-center justify-center w-14 h-14 rounded-xl bg-white border"
        style={{ borderColor: ton.bord }}
      >
        <span className="text-[11px] font-bold uppercase" style={{ color: ton.accent }}>
          {jourCourt(d)}
        </span>
        <span className="text-xl font-extrabold text-[#0F172A] leading-none">{d.j}</span>
      </div>
      <div className="flex flex-col gap-0.5 min-w-0">
        <span className="text-[15px] font-bold">
          Rendez-vous le {dateCourte(d)}
          {h ? ` à ${h}` : ""}
          {p.ville_depot ? ` · ${p.ville_depot}` : ""}
        </span>
        <span className="text-[13px] font-bold" style={{ color: ton.accent }}>
          {c.texte}
        </span>
      </div>
    </section>
  );
}

export default function EcranFiche({ code, groupe: groupeInitial, demandeId, onRetour, onOuvrirMembre, onConsentement, onLogout }) {
  const [groupe, setGroupe] = useState(groupeInitial);
  const [erreur, setErreur] = useState("");
  const maintenant = useMaintenant();
  const onLogoutRef = useRef(onLogout);
  useEffect(() => {
    onLogoutRef.current = onLogout;
  }, [onLogout]);

  // Ouverture depuis le bandeau RDV : le groupe n'est pas dans la liste chargée -> on le demande
  useEffect(() => {
    if (groupe) return undefined;
    const ctrl = new AbortController();
    axios
      .get(`${API}/mobile/groupe/${encodeURIComponent(demandeId)}`, {
        headers: entetes(code),
        signal: ctrl.signal,
        timeout: 30000,
      })
      .then(({ data }) => setGroupe(data.groupe))
      .catch((e) => {
        if (estAnnulation(e)) return;
        if (estNonAutorise(e)) {
          onLogoutRef.current();
          return;
        }
        setErreur(messageErreur(e, "Fiche introuvable."));
      });
    return () => ctrl.abort();
  }, [groupe, demandeId, code]);

  const p = groupe ? groupe.membres.find((m) => m.id === demandeId) || groupe.membres[0] : null;

  if (!p) {
    return (
      <div className="ecran flex flex-col bg-[#F4F6F9] text-[#0F172A]">
        <header className="flex-none sticky top-0 z-10 bg-[#0F172A] px-4 pb-3 pt-[max(12px,env(safe-area-inset-top))] flex items-center gap-2">
          <button
            type="button"
            onClick={onRetour}
            aria-label="Retour à la liste"
            className={`w-11 h-11 -ml-2.5 flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}
          >
            <ChevronLeft aria-hidden="true" className="w-[22px] h-[22px]" />
          </button>
          <span className="text-white font-bold">Fiche client</span>
        </header>
        <p role={erreur ? "alert" : "status"} className="m-0 p-8 text-center text-sm text-[#475569] flex items-center justify-center gap-2">
          {erreur || (
            <>
              <Loader2 aria-hidden="true" className="w-4 h-4 animate-spin" /> Chargement…
            </>
          )}
        </p>
      </div>
    );
  }

  const cat = categorie(p);
  const statut = libelleStatut(p, cat);
  const autres = groupe.membres.filter((m) => m.id !== p.id);
  const refDossier = (p.ref_groupe || p.ref_capago || "").trim();
  const incomplet = capagoIncomplete(p.ref_capago);
  const versement = Number(p.versement) || 0;
  const reste = Number(p.reste) || 0;
  const totalPaiement = versement + reste;
  const tel = (p.telephone || "").trim();
  const typeMotif = [p.type_demande, p.motif].filter(Boolean).join(" · ");
  const traces = [
    p.date_creation ? `1ère création le ${p.date_creation}` : "",
    p.date_saisie || p.saisi_par ? `Saisi${p.date_saisie ? ` le ${p.date_saisie}` : ""}${p.saisi_par ? ` par ${nomAgent(p.saisi_par)}` : ""}` : "",
    p.modifie_par ? `Modifié par ${nomAgent(p.modifie_par)}` : "",
  ].filter(Boolean);
  const action = `h-14 rounded-xl text-white flex flex-col items-center justify-center gap-0.5 text-xs font-bold ${FOCUS}`;

  return (
    <div className="ecran flex flex-col bg-[#F4F6F9] text-[#0F172A]" data-testid="ecran-fiche">
      <header className="flex-none sticky top-0 z-10 bg-[#0F172A] px-4 pb-[18px] pt-[max(12px,env(safe-area-inset-top))] flex flex-col gap-3.5">
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={onRetour}
            aria-label="Retour"
            data-testid="btn-retour-fiche"
            className={`w-11 h-11 -ml-2.5 flex-none flex items-center justify-center text-[#E2E8F0] rounded-lg ${FOCUS}`}
          >
            <ChevronLeft aria-hidden="true" className="w-[22px] h-[22px]" />
          </button>
          <span className="text-[#94A3B8] text-[13px] flex-1 min-w-0 truncate">
            {groupe.nb_membres > 1
              ? `Dossier ${refDossier || "groupe"} · ${groupe.nb_membres} membres`
              : "Fiche individuelle"}
          </span>
        </div>
        <div className="flex flex-col gap-2">
          <h1 className="m-0 text-white text-2xl font-extrabold break-words" data-testid="fiche-nom">
            {nomComplet(p)}
          </h1>
          <div className="flex flex-wrap gap-1.5">
            {statut ? (
              <span className="text-xs font-bold rounded-lg px-2 py-1" style={styleStatut(p.statut, cat)}>
                {statut}
              </span>
            ) : null}
            {p.statut_paiement ? (
              <span className="text-xs font-bold rounded-lg px-2 py-1" style={stylePaiement(p.statut_paiement)}>
                {p.statut_paiement}
              </span>
            ) : null}
            {typeMotif ? (
              <span className="text-xs font-bold rounded-lg px-2 py-1 bg-[#1E293B] text-[#CBD5E1]">{typeMotif}</span>
            ) : null}
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          {tel ? (
            <a href={lienTel(tel)} data-testid="btn-appeler" className={`${action} bg-[#1D4ED8]`}>
              <Phone aria-hidden="true" className="w-5 h-5" />
              Appeler
            </a>
          ) : (
            <button type="button" disabled className={`${action} bg-[#1D4ED8] opacity-40`}>
              <Phone aria-hidden="true" className="w-5 h-5" />
              Appeler
            </button>
          )}
          {tel ? (
            <a
              href={`https://wa.me/${numeroWhatsapp(tel)}`}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="btn-whatsapp"
              className={`${action} bg-[#047857]`}
            >
              <MessageCircle aria-hidden="true" className="w-5 h-5" />
              WhatsApp
            </a>
          ) : (
            <button type="button" disabled className={`${action} bg-[#047857] opacity-40`}>
              <MessageCircle aria-hidden="true" className="w-5 h-5" />
              WhatsApp
            </button>
          )}
          <button
            type="button"
            onClick={() => onConsentement(p)}
            data-testid="btn-consentement"
            className={`${action} bg-[#0F766E]`}
          >
            <PenLine aria-hidden="true" className="w-5 h-5" />
            Consentement
          </button>
        </div>
      </header>

      <main className="flex-1 min-h-0 overflow-y-auto overscroll-contain flex flex-col gap-3 p-3 [&>*]:shrink-0">
        <SectionRdv p={p} maintenant={maintenant} />

        {rdvBotASaisir(p) ? (
          <section
            data-testid="badge-rdv-bot"
            className="bg-[#FFFBEB] border border-[#FCD34D] rounded-[14px] p-3.5 flex items-start gap-3"
          >
            <Bot aria-hidden="true" className="w-5 h-5 text-[#92400E] flex-none mt-0.5" />
            <div className="flex flex-col gap-0.5 min-w-0">
              <span className="text-[15px] font-bold text-[#92400E]">RDV détecté par le bot — date à saisir</span>
              <span className="text-[13px] text-[#334155] break-words">{p.rdv_bot}</span>
            </div>
          </section>
        ) : null}

        {p.ref_capago || p.ref_france_visas || p.type_document ? (
          <section className="bg-white border border-[#E2E8F0] rounded-[14px] px-3.5 py-1">
            <h2 className={`${TITRE_SECTION} mt-2.5 mb-1`}>Références</h2>
            {p.ref_capago ? (
              <Ligne libelle="Capago" dernier={!p.ref_france_visas && !p.type_document && !incomplet}>
                <span className="flex flex-col items-end">
                  <span className={`font-mono text-[15px] font-bold ${incomplet ? "text-[#9A3412]" : ""}`}>{p.ref_capago}</span>
                  {p.date_capago ? <span className="text-xs text-[#475569]">attribuée le {p.date_capago}</span> : null}
                </span>
              </Ligne>
            ) : null}
            {incomplet ? (
              <div
                data-testid="badge-capago-incomplet"
                className="flex items-center gap-1.5 my-2 text-xs font-semibold text-[#9A3412] bg-[#FFF7ED] border border-[#FDBA74] rounded-lg px-2.5 py-2"
              >
                <AlertTriangle aria-hidden="true" className="w-4 h-4 flex-none" />
                Réf. Capago incomplète — à corriger (attendu : DZ + 6 caractères)
              </div>
            ) : null}
            {p.ref_france_visas ? (
              <Ligne libelle="France-Visas" dernier={!p.type_document}>
                <span className="font-mono text-[15px] font-bold break-all">{p.ref_france_visas}</span>
              </Ligne>
            ) : null}
            {p.type_document ? (
              <Ligne libelle="Document présenté" dernier>
                <span className="text-[15px] font-semibold">{p.type_document}</span>
              </Ligne>
            ) : null}
          </section>
        ) : null}

        {tel || p.email ? (
          <section className="bg-white border border-[#E2E8F0] rounded-[14px] px-3.5 py-1">
            <h2 className={`${TITRE_SECTION} mt-2.5 mb-1`}>Contact</h2>
            {tel ? (
              <Ligne libelle="Téléphone" dernier={!p.email}>
                <span className="text-[15px] font-semibold">{tel}</span>
              </Ligne>
            ) : null}
            {p.email ? (
              <Ligne libelle="E-mail" dernier>
                <span className="text-[15px] font-semibold break-all">{p.email}</span>
              </Ligne>
            ) : null}
          </section>
        ) : null}

        {versement > 0 || reste > 0 ? (
          <section className="bg-white border border-[#E2E8F0] rounded-[14px] p-3.5 flex flex-col gap-2">
            <h2 className={TITRE_SECTION}>Paiement</h2>
            <div className="flex justify-between gap-2 text-sm flex-wrap">
              <span>
                Versé <strong>{montant(versement)}</strong>
              </span>
              <span className="text-[#475569]">
                Reste <strong className="text-[#0F172A]">{montant(reste)}</strong>
              </span>
            </div>
            <div aria-hidden="true" className="h-2 rounded bg-[#E2E8F0] overflow-hidden">
              <div
                className="h-2"
                style={{
                  width: `${totalPaiement ? Math.round((versement / totalPaiement) * 100) : 0}%`,
                  background: reste > 0 ? "#B45309" : "#047857",
                }}
              />
            </div>
          </section>
        ) : null}

        {autres.length ? (
          <section className="bg-white border border-[#E2E8F0] rounded-[14px] px-3.5 py-1">
            <h2 className={`${TITRE_SECTION} mt-2.5 mb-1`}>Membres du groupe</h2>
            <ul className="list-none m-0 p-0">
              {autres.map((m, i) => (
                <li key={m.id} className={i < autres.length - 1 ? "border-b border-[#F1F5F9]" : ""}>
                  <button
                    type="button"
                    onClick={() => onOuvrirMembre(groupe, m.id)}
                    data-testid="membre-groupe"
                    className={`w-full min-h-[48px] py-1.5 flex items-center justify-between gap-2 text-left text-[#0F172A] rounded-lg ${FOCUS}`}
                  >
                    <span className="flex flex-col min-w-0">
                      <span className="text-[15px] font-semibold truncate">{nomComplet(m)}</span>
                      <span className="text-xs text-[#475569] truncate">
                        {[m.ref_capago, m.date_rdv ? resumeRdv(m.date_rdv) : m.statut].filter(Boolean).join(" · ") || "—"}
                      </span>
                    </span>
                    <ChevronRight aria-hidden="true" className="w-[18px] h-[18px] text-[#64748B] flex-none" />
                  </button>
                </li>
              ))}
            </ul>
          </section>
        ) : null}

        {traces.length ? (
          <p className="mx-1 mb-2 mt-0 text-xs text-[#475569] leading-normal">{traces.join(" · ")}</p>
        ) : null}
      </main>
    </div>
  );
}
